<template>
    <div class="knowledge-base-tab">
        <div class="tab-header">
            <h3>{{ $t("voiceagent.knowledgeBase") }}</h3>
            <p>
                {{ $t("voiceagent.configureKnowledgeBasesForAgent") }}
            </p>
        </div>

        <a-spin :spinning="loading">
            <!-- Bilgi tabanı seçimleri -->
            <div class="knowledge-base-section">
                <div class="section-header">
                    <h4>{{ $t("voiceagent.linkedKnowledgeBases") }}</h4>
                    <PermissionController :permissions="['voice-agent.create']">
                        <SupsisButton primary @click="showAddKnowledgeBase" :disabled="!agent.llm" icon="add">
                            {{ $t("voiceagent.addKnowledgeBase") }}
                        </SupsisButton>
                    </PermissionController>
                </div>

                <!-- Bağlı bilgi tabanları listesi -->
                <div
                    v-if="
                        !loading && agent.llm && agent.llm.knowledge_base_ids && agent.llm.knowledge_base_ids.length > 0
                    "
                    class="knowledge-base-list"
                >
                    <a-table
                        :columns="kbTableColumns"
                        :data-source="linkedKnowledgeBases"
                        :pagination="false"
                        :loading="tableLoading"
                        rowKey="knowledge_base_id"
                    >
                        <template slot="action" slot-scope="text, record">
                            <PermissionController :permissions="['voice-agent.delete']">
                                <SupsisButton
                                    danger
                                    icon="delete"
                                    small
                                    @click="removeKnowledgeBase(record.knowledge_base_id)"
                                ></SupsisButton>
                            </PermissionController>
                        </template>
                    </a-table>
                </div>
                <div
                    v-else-if="
                        !loading &&
                        (!agent.llm || !agent.llm.knowledge_base_ids || agent.llm.knowledge_base_ids.length === 0)
                    "
                    class="empty-state"
                >
                    <a-empty :description="$t('voiceagent.noLinkedKnowledgeBaseForAgent')">
                        <PermissionController :permissions="['voice-agent.create']">
                            <SupsisButton primary @click="showAddKnowledgeBase" :disabled="!agent.llm">
                                {{ $t("voiceagent.addKnowledgeBase") }}
                            </SupsisButton>
                        </PermissionController>
                    </a-empty>
                </div>
            </div>
        </a-spin>

        <!-- Bilgi tabanı ekleme modal -->
        <a-modal v-model="showModal" :title="$t('voiceagent.addKnowledgeBaseTitle')" :footer="null" @cancel="closeModal">
            <a-spin :spinning="modalLoading" class="kb-modal-content">
                <p>{{ $t("voiceagent.selectKbDescription") }}</p>

                <div v-if="availableKnowledgeBases.length > 0" class="available-kbs-list">
                    <a-radio-group v-model="selectedKnowledgeBaseId">
                        <a-list :data-source="availableKnowledgeBases" class="knowledge-base-select-list w-100">
                            <a-list-item slot="renderItem" slot-scope="item" class="kb-list-item">
                                <div class="kb-radio-wrapper">
                                    <a-radio :value="item.knowledge_base_id"></a-radio>
                                    <div class="kb-item">
                                        <div class="kb-name">{{ item.knowledge_base_name }}</div>
                                        <div class="kb-info">
                                            <a-tag>{{
                                                $t("voiceagent.nSources", {
                                                    count: item.knowledge_base_sources
                                                        ? item.knowledge_base_sources.length
                                                        : 0,
                                                })
                                            }}</a-tag>
                                            <a-tag :color="getStatusColor(item.status)"
                                                >{{ getStatusText(item.status) }}
                                            </a-tag>
                                        </div>
                                    </div>
                                </div>
                            </a-list-item>
                        </a-list>
                    </a-radio-group>
                </div>
                <div v-else class="no-available-kbs">
                    <a-empty :description="$t('voiceagent.noKbToAdd')">
                        <SupsisButton primary @click="navigateToKnowledgeBase">{{ $t("voiceagent.createKb") }}</SupsisButton>
                    </a-empty>
                </div>

                <div class="modal-footer">
                    <SupsisButton @click="closeModal">{{ $t("voiceagent.cancel") }}</SupsisButton>
                    <SupsisButton
                        primary
                        :disabled="!selectedKnowledgeBaseId || modalLoading"
                        @click="addKnowledgeBase"
                        :class="{ 'opacity-50': submitLoading }"
                    >
                        <i v-if="submitLoading" class="material-icons spin">autorenew</i>
                        {{ $t("voiceagent.add") }}
                    </SupsisButton>
                </div>
            </a-spin>
        </a-modal>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../../../api/voiceAgent";

import _ from "lodash";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";

export default {
    name: "KnowledgeBaseTab",
    components: {
        SupsisButton,
    },
    props: {
        agent: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            loading: false,
            tableLoading: false,
            modalLoading: false,
            submitLoading: false,
            showModal: false,
            allKnowledgeBases: [],
            linkedKnowledgeBases: [],
            selectedKnowledgeBaseId: null,
        };
    },
    computed: {
        kbTableColumns() {
            return [
                {
                    title: this.$t("voiceagent.kbName"),
                    dataIndex: "knowledge_base_name",
                    key: "knowledge_base_name",
                },
                {
                    title: this.$t("voiceagent.statusLabel"),
                    dataIndex: "status",
                    key: "status",
                    scopedSlots: {
                        customRender: "status",
                    },
                    render: (text, record) => (
                        <a-tag color={this.getStatusColor(record.status)}>{this.getStatusText(record.status)}</a-tag>
                    ),
                },
                {
                    title: this.$t("voiceagent.sourceCountColumn"),
                    key: "sources",
                    scopedSlots: {
                        customRender: "sources",
                    },
                    render: (text, record) => {
                        const sources = record.knowledge_base_sources || [];
                        return sources.length;
                    },
                },
                {
                    title: this.$t("voiceagent.actions"),
                    key: "action",
                    scopedSlots: { customRender: "action" },
                },
            ];
        },
        availableKnowledgeBases() {
            // Ajana henüz eklenmemiş bilgi tabanlarını filtrele
            if (!this.agent || !this.agent.llm || !this.agent.llm.knowledge_base_ids) {
                return this.allKnowledgeBases;
            }

            return this.allKnowledgeBases.filter(
                (kb) => !this.agent.llm.knowledge_base_ids.includes(kb.knowledge_base_id)
            );
        },
    },
    watch: {
        agent: {
            handler(newValue) {
                if (newValue) {
                    this.loadLinkedKnowledgeBases();
                }
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        async loadAllKnowledgeBases() {
            try {
                this.tableLoading = true;
                const response = await VOICE_AGENT_API.knowledgeBases.list();
                this.allKnowledgeBases = response || [];
            } catch (error) {
                console.error("Bilgi tabanları yüklenirken hata:", error);
                this.$message.error(this.$t("voiceagent.errorLoadingKbList"));
            } finally {
                this.tableLoading = false;
            }
        },
        async loadLinkedKnowledgeBases() {
            if (
                !this.agent ||
                !this.agent.llm ||
                !this.agent.llm.knowledge_base_ids ||
                this.agent.llm.knowledge_base_ids.length === 0
            ) {
                this.linkedKnowledgeBases = [];
                return;
            }

            try {
                this.loading = true;

                // Tüm bilgi tabanlarını yükle
                await this.loadAllKnowledgeBases();

                // Agent'a bağlı bilgi tabanlarını filtrele
                this.linkedKnowledgeBases = this.allKnowledgeBases.filter((kb) =>
                    this.agent.llm.knowledge_base_ids.includes(kb.knowledge_base_id)
                );
            } catch (error) {
                console.error("Bağlı bilgi tabanları yüklenirken hata:", error);
                this.$message.error(this.$t("voiceagent.errorLoadingLinkedKb"));
            } finally {
                this.loading = false;
            }
        },
        showAddKnowledgeBase() {
            if (!this.agent || !this.agent.llm) {
                this.$message.warning(this.$t("voiceagent.needLlmConfigFirst"));
                return;
            }

            this.showModal = true;
            this.selectedKnowledgeBaseId = null;
            this.modalLoading = true;

            this.loadAllKnowledgeBases().finally(() => {
                this.modalLoading = false;
            });
        },
        closeModal() {
            this.showModal = false;
            this.selectedKnowledgeBaseId = null;
        },
        async addKnowledgeBase() {
            if (!this.selectedKnowledgeBaseId || !this.agent || !this.agent.llm) {
                return;
            }

            try {
                this.submitLoading = true;

                // Mevcut knowledge_base_ids dizisini al
                const currentKbIds = Array.isArray(this.agent.llm.knowledge_base_ids)
                    ? [...this.agent.llm.knowledge_base_ids]
                    : [];

                // Seçilen id'yi ekle
                if (!currentKbIds.includes(this.selectedKnowledgeBaseId)) {
                    currentKbIds.push(this.selectedKnowledgeBaseId);
                }

                // LLM güncelleme verisi
                const llmData = {
                    knowledge_base_ids: currentKbIds,
                };

                // LLM güncelleme API çağrısı
                await VOICE_AGENT_API.llms.update(this.agent.response_engine.llm_id, llmData);

                // UI ve kullanıcıya bildirim
                this.$message.success(this.$t("voiceagent.kbAdded"));

                // Üst bileşene bildir (AgentEditor üzerinden agent güncellemesi için)
                this.$emit("llm-updated", {
                    ...this.agent.llm,
                    knowledge_base_ids: currentKbIds,
                });

                // Modal kapat
                this.closeModal();

                // Bağlı bilgi tabanlarını yeniden yükle
                this.loadLinkedKnowledgeBases();
            } catch (error) {
                console.error("Bilgi tabanı eklenirken hata:", error);
                this.$message.error(this.$t("voiceagent.kbAddFailed"));
            } finally {
                this.submitLoading = false;
            }
        },
        async removeKnowledgeBase(knowledgeBaseId) {
            if (!this.agent || !this.agent.llm || !this.agent.response_engine || !this.agent.response_engine.llm_id) {
                return;
            }

            try {
                this.loading = true;
                // Kullanıcıya onay sor
                await this.$confirm({
                    title: this.$t("voiceagent.removeKbTitle"),
                    content: this.$t("voiceagent.removeKbConfirm"),
                    okText: this.$t("voiceagent.yes"),
                    cancelText: this.$t("voiceagent.no"),
                    okType: "danger",
                    onOk: async () => {
                        // Mevcut knowledge_base_ids dizisinden çıkar
                        const currentKbIds = Array.isArray(this.agent.llm.knowledge_base_ids)
                            ? [...this.agent.llm.knowledge_base_ids]
                            : [];

                        const updatedKbIds = currentKbIds.filter((id) => id !== knowledgeBaseId);

                        // LLM güncelleme verisi
                        const llmData = {
                            knowledge_base_ids: updatedKbIds.length > 0 ? updatedKbIds : null,
                        };

                        // LLM güncelleme API çağrısı
                        await VOICE_AGENT_API.llms.update(this.agent.response_engine.llm_id, llmData);

                        // UI ve kullanıcıya bildirim
                        this.$message.success(this.$t("voiceagent.kbRemoved"));

                        // Üst bileşene bildir (AgentEditor üzerinden agent güncellemesi için)
                        this.$emit("llm-updated", {
                            ...this.agent.llm,
                            knowledge_base_ids: updatedKbIds.length > 0 ? updatedKbIds : null,
                        });

                        // Bağlı bilgi tabanlarını yeniden yükle
                        this.loadLinkedKnowledgeBases();
                    },
                    onCancel: () => {
                        this.loading = false;
                    },
                });
            } catch (error) {
                if (error && typeof error === "object" && "canceled" in error) {
                    // İşlem kullanıcı tarafından iptal edildi
                    return;
                }

                console.error("Bilgi tabanı kaldırılırken hata:", error);
                this.$message.error(this.$t("voiceagent.kbRemoveFailed"));
            } finally {
                this.loading = false;
            }
        },
        getStatusColor(status) {
            if (status === "complete") return "green";
            if (status === "in_progress") return "blue";
            if (status === "error") return "red";
            return "default";
        },
        getStatusText(status) {
            if (status === "complete") return this.$t("voiceagent.statusCompleted");
            if (status === "in_progress") return this.$t("voiceagent.statusProcessing");
            if (status === "error") return this.$t("voiceagent.statusErrorShort");
            return status;
        },
        navigateToKnowledgeBase() {
            var channelId = this.$route.params.id || this.$route.params.channelId;
            if (!channelId) {
                return;
            }
            this.$router.push({
                name: "voice-agent-dashboard-knowledge-base",
                params: {
                    id: channelId,
                },
                query: {
                    tab: "knowledge-base",
                },
            });
        },
    },
};
</script>

<style lang="scss">
.kb-modal-content {
    .knowledge-base-select-list {
        max-height: 300px;
        overflow-y: auto;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        width: 100%;

        .kb-list-item {
            padding: 8px 12px;
            width: 100%;

            &:hover {
                background-color: #f5f5f5;
            }
        }

        .kb-radio-wrapper {
            width: 100%;
            display: flex;
            align-items: center;

            .ant-radio-wrapper {
                margin-right: 12px;
                padding-top: 2px;
            }
        }

        .kb-item {
            display: flex;
            flex-direction: column;
            flex: 1;

            .kb-name {
                font-weight: 500;
                margin-bottom: 4px;
            }

            .kb-info {
                margin-top: 4px;

                .ant-tag {
                    margin-right: 5px;
                }
            }
        }
    }

    .no-available-kbs {
        padding: 30px 0;
    }
}

.knowledge-base-tab {
    padding: 20px 0;

    .tab-header {
        margin-bottom: 20px;

        h3 {
            margin: 0 0 10px 0;
            font-weight: 500;
        }

        p {
            color: #666;
            margin: 0;
        }
    }

    .knowledge-base-section {
        background-color: #f9f9f9;
        padding: 15px;
        border-radius: 4px;
        margin-bottom: 20px;
        border: 1px solid #e8e8e8;

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;

            h4 {
                margin: 0;
                font-weight: 500;
            }
        }
    }

    .empty-state {
        padding: 30px 0;
        text-align: center;
    }

    .modal-footer {
        margin-top: 20px;
        text-align: right;

        .supsis-button {
            margin-left: 8px;
        }
    }

    .spin {
        animation: spin 1.5s infinite linear;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .opacity-50 {
        opacity: 0.5;
        pointer-events: none;
    }
}
</style>
